import React, { useState, useEffect, useRef } from 'react'
import { Player } from '@lottiefiles/react-lottie-player'
import cx from 'classnames'
import { gsap } from 'gsap'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { useInView } from 'react-intersection-observer'

import BorderAnimation from 'theme/animations/branding/line.json'
import TitleOutlineAnimation from 'theme/animations/branding/branding-outline.json'
import CursorAnimation from 'theme/animations/branding/cursor.json'

gsap.registerPlugin(MotionPathPlugin)

function SwatchButton({
  swatchRef,
  index,
  color,
  name,
  onClick,
  isSelected,
  isSwatchesActive,
  isCursorActive,
}) {
  const colorRef = useRef(null)

  useEffect(() => {
    if (isSwatchesActive && !isNil(colorRef.current)) {
      gsap.to(colorRef.current, {
        opacity: 1,
        duration: 0.75,
        ease: 'power2.out',
        delay: 0.25 * index,
      })
    }
  }, [isSwatchesActive, colorRef.current])

  function onSelect() {
    onClick(color)
  }

  return (
    <button
      className={cx('branding-hero__swatch-button', {
        '--selected': isSelected,
        '--is-fake-hover': isCursorActive && isSelected,
      })}
      type="button"
      onClick={onSelect}
      ref={swatchRef}
    >
      <div ref={colorRef} className={`branding-hero__swatch --${name}`} />
      <span className="branding-hero__swatch-loader">
        <svg viewBox="25 25 50 50">
          <circle
            className="swatch__loader-path"
            cx="50"
            cy="50"
            r="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      </span>
      <span className="branding-hero__swatch-color">{color}</span>
    </button>
  )
}

SwatchButton.propTypes = {
  swatchRef: PropTypes.object,
  color: PropTypes.string,
  onClick: PropTypes.func,
  name: PropTypes.string,
  isSelected: PropTypes.bool,
  isSwatchesActive: PropTypes.bool,
  isCursorActive: PropTypes.bool,
  index: PropTypes.number,
}

function BrandingHero() {
  const timelineRef = useRef(null)
  const cursorRef = useRef(null)
  const cursorAnimationRef = useRef(null)
  const swatchOneRef = useRef(null)
  const swatchTwoRef = useRef(null)
  const swatchThreeRef = useRef(null)
  const swatchFourRef = useRef(null)
  const [currentColor, setCurrentColor] = useState('')
  const [isSwatchesActive, setSwatchesActive] = useState(false)
  const [isCursorActive, setCursorActive] = useState(false)
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.65,
  })

  useEffect(() => {
    setTimeout(() => {
      setSwatchesActive(true)
    }, 1000)
  }, [])

  function onSelectColor(color) {
    setCurrentColor(color)
  }

  function onTitleOutLineEvent(event) {
    if (event === 'complete') {
      const deltaOne = MotionPathPlugin.getRelativePosition(
        cursorRef.current,
        swatchOneRef.current,
        [0.5, 0.5],
        [0.75, 1],
      )
      timelineRef.current = gsap.timeline()
      timelineRef.current.to(cursorRef.current, {
        x: `+=${deltaOne.x}`,
        y: `+=${deltaOne.y}`,
        duration: 0.75,
        ease: 'power2.inOut',
        onComplete: () => {
          setCursorActive(true)
          cursorAnimationRef.current.play()
          setCurrentColor('#7838FF')
        },
      })
      timelineRef.current.to(
        cursorRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
        },
        '-=0.9',
      )
    }
  }

  function onCompleteClick(event) {
    if (event === 'complete' && currentColor === '#7838FF') {
      const deltaTwo = MotionPathPlugin.getRelativePosition(
        cursorRef.current,
        swatchTwoRef.current,
        [0.5, 0.5],
        [0.75, 1],
      )
      cursorAnimationRef.current.pause()
      cursorAnimationRef.current.setSeeker(0)
      timelineRef.current.to(cursorRef.current, {
        x: `+=${deltaTwo.x}`,
        duration: 1,
        ease: 'power2.inOut',
        onComplete: () => {
          cursorAnimationRef.current.play()
          setCurrentColor('#1e1e1e')
        },
      })
    }
    if (event === 'complete' && currentColor === '#1e1e1e') {
      timelineRef.current.to(cursorRef.current, {
        opacity: 0,
        duration: 0.35,
        delay: 0.5,
        ease: 'power2.out',
        onStart: () => {
          setCursorActive(false)
        },
      })
    }
  }

  return (
    <header
      ref={inViewRef}
      className={cx('branding-hero', {
        '--in-view': inView,
      })}
    >
      <div className="branding-hero__container">
        <div className="branding-hero__swatch-picker">
          <SwatchButton
            index={0}
            color="#7838FF"
            name="studio-purple"
            onClick={onSelectColor}
            swatchRef={swatchOneRef}
            isSelected={currentColor === '#7838FF'}
            isSwatchesActive={isSwatchesActive}
            isCursorActive={isCursorActive}
          />
          <SwatchButton
            index={1}
            color="#1e1e1e"
            name="studio-grey"
            swatchRef={swatchTwoRef}
            onClick={onSelectColor}
            isSelected={currentColor === '#1e1e1e'}
            isSwatchesActive={isSwatchesActive}
            isCursorActive={isCursorActive}
          />
          <SwatchButton
            index={2}
            color="#F5F1F1"
            name="studio-white"
            swatchRef={swatchThreeRef}
            onClick={onSelectColor}
            isSelected={currentColor === '#F5F1F1'}
            isSwatchesActive={isSwatchesActive}
          />
          <SwatchButton
            index={3}
            color="#FB3640"
            name="studio-red"
            swatchRef={swatchFourRef}
            onClick={onSelectColor}
            isSelected={currentColor === '#FB3640'}
            isSwatchesActive={isSwatchesActive}
          />
        </div>
        <div className="branding-hero__title">
          <div
            className={cx('branding-hero__title-outline', {
              '--is-active':
                currentColor === '#F5F1F1' || isEmpty(currentColor),
            })}
          >
            <Player
              autoplay
              src={TitleOutlineAnimation}
              keepLastFrame
              onEvent={onTitleOutLineEvent}
            />
          </div>
          <h1
            className={cx({
              '--studio-white': currentColor === '#F5F1F1',
            })}
            style={{ color: currentColor }}
          >
            Branding
          </h1>
        </div>
        <div className="branding-hero__cursor" ref={cursorRef}>
          <Player
            ref={cursorAnimationRef}
            src={CursorAnimation}
            onEvent={onCompleteClick}
            keepLastFrame
          />
        </div>
      </div>
      <div className="branding-hero__border">
        <Player autoplay src={BorderAnimation} keepLastFrame />
      </div>
    </header>
  )
}

export default BrandingHero
